/**
 * A service for controlling the timestamps for today
 */

import { Injectable, inject } from '@angular/core';
import { TimestampRepository } from './timestamps.repository';
import { HistoryRepository } from 'src/app/analytics/state/history.repository';
import { differenceInSeconds, getSeconds } from 'date-fns';
import { FirebaseSyncService } from 'src/app/services/firebase-sync/firebase-sync.service';
import { SettingsRepository } from 'src/app/settings/state/settings.repository';
import { TimerAction, TimestampEntity } from './timer.interface';
import { calculateSessionTime } from 'src/app/utilities/timestamp.utils';
import { boundaryFor, dayKeyFor, startBoundaryFor } from 'src/app/utilities/day-key.utils';

export type HistoryEditValidation =
  | { valid: true }
  | {
      valid: false;
      reason: string;
      entryIndex?: number;
    };

@Injectable({
  providedIn: 'root',
})
export class TimestampService {
  private timestamps = inject(TimestampRepository);
  private history = inject(HistoryRepository);
  private sync = inject(FirebaseSyncService);
  private settings = inject(SettingsRepository);

  /** The logical day currently in progress. Every "today" mutation routes through this. */
  currentDayKey(): string {
    return dayKeyFor(new Date(), this.settings.getDayStartHour());
  }

  setMultiplier(multiplier: number) {
    const today = this.currentDayKey();

    this.history.addSettings(
      { multiplier: multiplier, goal: this.timestamps.getTodaysGoal().goal },
      today,
    );

    this.sync.scheduleSaveDay(today);
  }

  getMultiplier() {
    return this.timestamps.getMultiplier();
  }

  //Goals
  setGoal(goalsInMinutes: number) {
    const today = this.currentDayKey();

    this.history.addSettings(
      { goal: goalsInMinutes, multiplier: this.timestamps.getMultiplier() },
      today,
    );

    this.sync.scheduleSaveDay(today);
  }

  addEntryAndSync(action: TimerAction, timestamp: string = new Date().toString()) {
    const dayStartHour = this.settings.getDayStartHour();
    // The entry's own timestamp decides its day — never the wall clock, never the active pointer.
    const dayKey = dayKeyFor(timestamp, dayStartHour);
    const entities = this.history.getDay(dayKey)?.timestamps ?? [];

    const lastEntry = entities.slice(-1)[0];

    // Do not add the same action twice. This dedupe is also what makes reconcile() idempotent.
    if (lastEntry && lastEntry.action === action) {
      return;
    }

    let id = new Date().getTime();
    while (entities.some((entity) => entity.id === id)) {
      id++;
    }

    this.history.appendEntry(dayKey, { timestamp, action, id }, dayStartHour);

    this.sync.scheduleSaveDay(dayKey);
  }

  deleteEntry(entryId: number) {
    const today = this.currentDayKey();
    const todaysEntities = this.history.getDay(today)?.timestamps ?? [];
    const newEntities = todaysEntities.filter((entity) => entity.id !== entryId);
    this.history.updateEntities(today, newEntities, this.settings.getDayStartHour());

    this.sync.scheduleSaveDay(today);
  }

  updateEntryTime(oldId: number, newTime: string | null) {
    if (!newTime) {
      return;
    }

    const today = this.currentDayKey();
    const todaysEntities = this.history.getDay(today)?.timestamps ?? [];

    // finds index of entity to be edited (usually the last one)
    const entityIndex = todaysEntities.findIndex((entity) => entity.id === oldId);

    if (entityIndex === -1) {
      return;
    }

    const entity = todaysEntities[entityIndex];
    let stopEntity = null; // needs to update this time as well
    let referenceEntity = todaysEntities[entityIndex - 1]; // refenrece time, can't go earlier than this

    // checks if there is a stop entity just before (or an reference entity)
    if (todaysEntities.length > 1 && todaysEntities[entityIndex - 1].action === TimerAction.Stop) {
      stopEntity = todaysEntities[entityIndex - 1];
      // if the stop entity is more than 2 seconds away from the entity, it is not a valid stop entity
      if (differenceInSeconds(stopEntity.timestamp, entity.timestamp) > 2) {
        stopEntity = null;
      }
      referenceEntity = todaysEntities[entityIndex - 2];
    }

    const [hours, minutes] = newTime.split(':');
    const newTimestamp = new Date(entity.timestamp);
    newTimestamp.setHours(parseInt(hours, 10));
    newTimestamp.setMinutes(parseInt(minutes, 10));
    newTimestamp.setSeconds(0);

    if (referenceEntity) {
      const referenceSeconds = getSeconds(referenceEntity.timestamp);
      newTimestamp.setSeconds(referenceSeconds + 1);
    }

    // I2: only the hour/minute changed above — the calendar date is inherited from the original
    // entry, so a time like 03:00 with a 4am boundary would otherwise land on logical yesterday
    // while staying in today's array. Clamp into [start, end) of `today` so this can never happen,
    // rather than relying solely on validateHistoryEdit to catch it at save time.
    const dayStartHour = this.settings.getDayStartHour();
    const dayStart = startBoundaryFor(today, dayStartHour);
    const dayEnd = boundaryFor(today, dayStartHour);
    if (newTimestamp < dayStart) {
      newTimestamp.setTime(dayStart.getTime());
    } else if (newTimestamp >= dayEnd) {
      // `today` is the day currently in progress, so `now` always falls in [dayStart, dayEnd) —
      // but when `now` itself is between midnight and dayStartHour, dayEnd is still hours in the
      // FUTURE. Clamping to dayEnd - 1s unconditionally would then write a future timestamp, which
      // validateHistoryEdit explicitly rejects. Never clamp past `now`.
      const now = new Date();
      newTimestamp.setTime(Math.min(dayEnd.getTime() - 1000, now.getTime()));
    }

    if (stopEntity) {
      stopEntity.timestamp = newTimestamp.toString();
    }

    const newId = newTimestamp.getTime();
    const newEntities = todaysEntities.map((en) => {
      if (en.id === stopEntity?.id) {
        // TODO: Fix the -1
        return { ...stopEntity, timestamp: stopEntity.timestamp, id: newId - 1 };
      }

      if (en.id === entity.id) {
        return { ...entity, timestamp: newTimestamp.toString(), id: newId };
      }
      return en;
    });

    this.history.updateEntities(today, newEntities, dayStartHour);
    this.sync.scheduleSaveDay(today);
  }

  /**
   * Replaces today's timestamp list in a single shot and triggers one Firestore write.
   * Used by the Adjust History sheet so multi-step edits don't sync intermediate state.
   */
  applyHistoryEdit(newEntities: TimestampEntity[], dayKey?: string): HistoryEditValidation {
    const validation = this.validateHistoryEdit(newEntities, dayKey);
    if (!validation.valid) {
      return validation;
    }

    const targetDay = dayKey ?? this.currentDayKey();
    this.history.updateEntities(targetDay, newEntities, this.settings.getDayStartHour());
    this.sync.scheduleSaveDay(targetDay);
    return validation;
  }

  /**
   * Validates a candidate timestamp list for today against:
   *  1. Chronology — never backwards, no future timestamps.
   *  2. Rest/Focus ratio — total rest ≤ total focus / multiplier.
   *
   * We intentionally do NOT enforce a strict state machine over the action sequence
   * (e.g. "Pause must follow a session start"). History is a record of what happened,
   * not a clean event log we control — natural app usage can produce orphan pauses,
   * back-to-back Focus entries, etc. TimerService and calculateSessionTime tolerate
   * those shapes; the user should not have to clean them up to save unrelated edits.
   */
  /**
   * Validates an edited day log. `dayKey` targets a specific (possibly past) logical day —
   * the Adjust History sheet passes it when browsing history day-by-day. Omitting it keeps
   * the original today-only semantics (today as the membership day, active-day multiplier),
   * which every pre-existing caller relies on.
   */
  validateHistoryEdit(newEntities: TimestampEntity[], dayKey?: string): HistoryEditValidation {
    const now = new Date();
    const dayStartHour = this.settings.getDayStartHour();
    const targetDayKey = dayKey ?? dayKeyFor(now, dayStartHour);
    const dayLabel = dayKey === undefined ? 'today' : 'this day';
    const storedDay = dayKey === undefined ? undefined : this.history.getDay(dayKey);
    // Stored days can legitimately hold entries whose timestamps map to a DIFFERENT logical
    // day: data recorded before the logical-day migration and re-adopted from remote is never
    // repartitioned (C3 adopt-time repartitioning was deliberately removed — see the 2026-08
    // decision records). Keeping such an entry exactly as stored makes nothing worse, and
    // rejecting it would make precisely the messy days this editor exists to repair
    // uneditable. Only MOVING an entry outside the day — the relocation I2 guards against —
    // is a new violation. Matched by action+timestamp: candidateEntities() rewrites ids.
    const grandfathered = new Set(
      (storedDay?.timestamps ?? []).map((t) => `${t.action}|${t.timestamp}`),
    );

    for (let i = 0; i < newEntities.length; i++) {
      const entry = newEntities[i];
      const entryDate = new Date(entry.timestamp);

      if (isNaN(entryDate.getTime())) {
        return { valid: false, reason: `Entry ${i + 1} has an invalid time.`, entryIndex: i };
      }

      if (entryDate > now) {
        return { valid: false, reason: `Entry ${i + 1} is in the future.`, entryIndex: i };
      }

      if (i > 0) {
        const prevDate = new Date(newEntities[i - 1].timestamp);
        if (entryDate < prevDate) {
          return {
            valid: false,
            reason: `Entry ${i + 1} must not be before the previous entry.`,
            entryIndex: i,
          };
        }
      }

      // I2: an edit must not relocate an entry outside the day it is being saved into — e.g.
      // pushing the last entry below dayStartHour would leave it stranded in today's array while
      // actually belonging to yesterday, splitting a Focus from its Stop across two days.
      if (
        dayKeyFor(entry.timestamp, dayStartHour) !== targetDayKey &&
        !grandfathered.has(`${entry.action}|${entry.timestamp}`)
      ) {
        return {
          valid: false,
          reason: `Entry ${i + 1} falls outside ${dayLabel} (which starts at ${dayStartHour}:00) — pick a time after that hour.`,
          entryIndex: i,
        };
      }
    }

    // A past day's rest allowance is judged by ITS stored multiplier, not the active day's.
    const multiplier =
      dayKey === undefined
        ? this.timestamps.getMultiplier()
        : (storedDay?.multiplier ?? this.timestamps.getMultiplier());
    const focusSeconds = calculateSessionTime(TimerAction.Focus, newEntities, dayStartHour);
    const restSeconds = calculateSessionTime(TimerAction.Rest, newEntities, dayStartHour);
    const allowedRest = focusSeconds / multiplier;

    if (restSeconds > allowedRest) {
      const allowedMinutes = Math.floor(allowedRest / 60);
      const restMinutes = Math.ceil(restSeconds / 60);
      return {
        valid: false,
        reason: `Rest (${restMinutes}m) exceeds the allowed ${allowedMinutes}m (focus ÷ ${multiplier}).`,
      };
    }

    return { valid: true };
  }
}
